import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Warning,
  SupportAgent,
  Refresh,
  Logout,
  AccessTime,
  Block,
  Payment,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../context/SubscriptionContext';

interface Props {
  open: boolean;
  onClose?: () => void;
}

const SubscriptionExpiredModal: React.FC<Props> = ({ open, onClose }) => {
  const theme = useTheme();
  const { logout } = useAuth();
  const { status, daysRemaining, plan, trialEndsAt } = useSubscription();

  const isTrial = status === 'trial';
  const isTrialEnded = status === 'expired' && trialEndsAt;
  const isExpired = status === 'expired';
  const isSuspended = status === 'suspended';
  const isPendingPayment = status === 'pending_payment';

  const getTitle = () => {
    if (isPendingPayment) return 'Payment Required';
    if (isSuspended) return 'Subscription Suspended';
    if (isTrialEnded) return 'Trial Period Ended';
    if (isExpired) return 'Subscription Expired';
    return 'Subscription Issue';
  };

  const getMessage = () => {
    if (isPendingPayment) {
      return 'Your school\'s subscription is pending payment. Please contact your school administrator to complete the payment and activate your account.';
    }
    if (isSuspended) {
      return 'Your school\'s subscription has been suspended by the administrator. Please contact your school administrator or support for assistance.';
    }
    if (isTrialEnded) {
      return 'Your trial period has ended. Please contact support to upgrade to a paid plan and continue using the platform.';
    }
    if (isExpired) {
      return 'Your subscription has expired. Please contact support to renew your subscription and continue using the platform.';
    }
    return 'There is an issue with your subscription. Please contact support for assistance.';
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleContactSupport = () => {
    // You can change this to your actual support email
    window.open('mailto:support@edubook.com?subject=Subscription%20Support', '_blank');
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      {/* Header with gradient */}
      <Box
        sx={{
          background: isPendingPayment
            ? `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`
            : isSuspended 
            ? `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`
            : `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.error.main} 100%)`,
          py: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: 'rgba(255, 255, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          {isPendingPayment ? (
            <Payment sx={{ fontSize: 40, color: 'white' }} />
          ) : isSuspended ? (
            <Block sx={{ fontSize: 40, color: 'white' }} />
          ) : isTrialEnded ? (
            <AccessTime sx={{ fontSize: 40, color: 'white' }} />
          ) : (
            <Warning sx={{ fontSize: 40, color: 'white' }} />
          )}
        </Box>
        <Typography variant="h5" fontWeight={700} color="white">
          {getTitle()}
        </Typography>
      </Box>

      <DialogContent sx={{ textAlign: 'center', py: 4, px: 4 }}>
        <Typography color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
          {getMessage()}
        </Typography>

        {plan && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              borderRadius: 2,
              mb: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Plan:
            </Typography>
            <Typography variant="body2" fontWeight={600} color="primary">
              {plan.name}
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            mt: 2,
            p: 2,
            bgcolor: alpha(theme.palette.info.main, 0.05),
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Need help? Contact our support team for immediate assistance with your subscription.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: 'center',
          pb: 4,
          px: 4,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Button
          variant="contained"
          color="primary"
          startIcon={<SupportAgent />}
          onClick={handleContactSupport}
          sx={{ borderRadius: 2, px: 3 }}
        >
          Contact Support
        </Button>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={handleRefresh}
          sx={{ borderRadius: 2 }}
        >
          Refresh
        </Button>
        <Button
          variant="text"
          color="inherit"
          startIcon={<Logout />}
          onClick={logout}
          sx={{ borderRadius: 2 }}
        >
          Logout
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubscriptionExpiredModal;
